import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  TextField,
  CircularProgress,
  Fade,
  Tooltip,
  InputAdornment
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import SearchIcon from '@mui/icons-material/Search';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import ScheduleIcon from '@mui/icons-material/Schedule';
import apiClient from '../utils/apiInterceptor';
import './AutoNovelPage.css';
import '../styles/NexusToast.css';

// 🔥 卡片主题色循环（深色底上的高饱和点缀色，卡片墙富有层次）
const ACCENTS = ['cyan', 'blue', 'violet', 'rose', 'emerald', 'amber'];

// 字数格式化
const fmtWords = (w) => {
  if (!w) return '0';
  if (w >= 100000000) return `${(w / 100000000).toFixed(1)}亿`;
  if (w >= 10000) return `${(w / 10000).toFixed(1)}万`;
  if (w >= 1000) return `${(w / 1000).toFixed(1)}k`;
  return String(w);
};

// 相对时间格式化
const fmtTime = (d) => {
  if (!d) return '--';
  try {
    const diff = Date.now() - new Date(d).getTime();
    if (diff < 60000) return '刚刚';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}分钟前`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}小时前`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}天前`;
    return new Date(d).toLocaleDateString('zh-CN', { month: '2-digit', day: '2-digit' });
  } catch { return '--'; }
};

/**
 * 🔥 项目卡片（React.memo 独立组件）
 *    - hover 效果纯 CSS 实现（此前 hoveredCard state 导致 hover 时整个列表重渲染）
 *    - 统计信息（章节/字数/更新时间）直接展示，信息一目了然
 */
const ProjectCard = React.memo(({
  novel,
  index,
  isEditing,
  editingTitle,
  onEditingTitleChange,
  onStartEdit,
  onSaveTitle,
  onCancelEdit,
  onDelete,
  onStart
}) => {
  const novelId = novel._id || novel.id;
  const accent = ACCENTS[index % ACCENTS.length];
  // 🔥 后端列表已瘦身：chapters 不再传输，优先用 chapterCount 数字
  const chapterCount = novel.chapterCount ?? novel.chapters?.length ?? 0;
  const wordCount = novel.totalWords || novel.wordCount || 0;
  const updated = fmtTime(novel.updatedAt || novel.updated);

  return (
    <Box className={`project-card${index < 12 ? ' card-enter' : ''}`} data-accent={accent}>
      <Box className="card-accent-bar" />
      <Box className="card-bg-glow" />

      <Box className="card-body">
        <Box className="card-icon-wrap">
          <AutoStoriesIcon className="card-icon" />
        </Box>

        <Box className="card-title-area">
          {isEditing ? (
            <Box className="title-edit-area" onClick={(e) => e.stopPropagation()}>
              <TextField
                className="title-input"
                value={editingTitle}
                onChange={(e) => onEditingTitleChange(e.target.value.slice(0, 30))}
                autoFocus
                size="small"
                fullWidth
                placeholder="输入项目名称"
              />
              <Box className="edit-btn-group">
                <IconButton className="save-btn" onClick={onSaveTitle}>
                  <SaveIcon sx={{ fontSize: 14 }} />
                </IconButton>
                <IconButton className="cancel-btn" onClick={onCancelEdit}>
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            </Box>
          ) : (
            <Box className="title-view-area" onClick={(e) => onStartEdit(e, novel)}>
              <Typography className="card-title">{novel.title || '未命名项目'}</Typography>
              <Tooltip title="重命名" placement="top">
                <IconButton className="edit-btn" size="small" onClick={(e) => onStartEdit(e, novel)}>
                  <EditIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </Box>
          )}
          <Typography className="card-desc">
            {novel.description || `${fmtTime(novel.createdAt)}创建 · 暂无简介`}
          </Typography>
        </Box>

        <Box className="card-index">#{String(index + 1).padStart(3, '0')}</Box>
      </Box>

      <Box className="card-stats">
        <Box className="stat-item">
          <Typography className="stat-num">{chapterCount}</Typography>
          <Typography className="stat-label">章节</Typography>
        </Box>
        <Box className="stat-divider" />
        <Box className="stat-item">
          <Typography className="stat-num">{fmtWords(wordCount)}</Typography>
          <Typography className="stat-label">字数</Typography>
        </Box>
        <Box className="stat-divider" />
        <Box className="stat-item stat-time">
          <ScheduleIcon sx={{ fontSize: 12 }} className="stat-time-icon" />
          <Typography className="stat-label">{updated}</Typography>
        </Box>
      </Box>

      <Box className="card-actions">
        <Button
          className="start-btn"
          startIcon={<PlayArrowIcon />}
          onClick={(e) => onStart(e, novelId)}
        >
          开始创作
        </Button>
        <Tooltip title="删除项目" placement="top">
          <IconButton className="delete-btn" onClick={(e) => onDelete(e, novel)}>
            <DeleteOutlineIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
});

const AutoNovelPage = () => {
  const navigate = useNavigate();
  const [novels, setNovels] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [newNovel, setNewNovel] = useState({ title: '', description: '' });
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [editingNovelId, setEditingNovelId] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, novelId: null, title: '' });
  // 🔥 搜索：小说多时快速定位项目
  const [searchText, setSearchText] = useState('');
  // 🔥 分批渲染：初始仅渲染首屏卡片，滚动到底部自动加载下一批（小说多时避免一次性渲染卡顿）
  const [visibleCount, setVisibleCount] = useState(12);
  const canvasRef = useRef(null);
  const sentinelRef = useRef(null);
  const BATCH_SIZE = 12;

  // 增强版粒子+网格背景动画（性能优化版）
  useEffect(() => {
    setMounted(true);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const isMobile = window.innerWidth < 768;
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    // 🔥 性能优化：粒子数按设备缩放（60→36/桌面、18/移动端），
    //    连线判定用距离平方比较（省去每对粒子的 sqrt），移动端禁用连线
    const particleCount = isMobile ? 18 : 36;
    const particles = [];
    const colors = [
      { h: 220, s: 90, l: 65 },
      { h: 200, s: 85, l: 60 },
      { h: 240, s: 80, l: 70 },
      { h: 270, s: 75, l: 65 },
      { h: 185, s: 85, l: 60 },
    ];

    for (let i = 0; i < particleCount; i++) {
      const color = colors[Math.floor(Math.random() * colors.length)];
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.4,
        vy: (Math.random() - 0.5) * 0.4,
        size: Math.random() * 2.5 + 0.5,
        opacity: Math.random() * 0.6 + 0.15,
        hue: color.h,
        saturation: color.s,
        lightness: color.l,
        pulsePhase: Math.random() * Math.PI * 2,
        pulseSpeed: Math.random() * 0.02 + 0.01,
      });
    }

    const LINK_DIST_SQ = 130 * 130;
    let animationId;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // 连线（仅桌面端；平方距离比较避免 sqrt）
      if (!isMobile) {
        for (let i = 0; i < particles.length; i++) {
          const p = particles[i];
          for (let j = i + 1; j < particles.length; j++) {
            const p2 = particles[j];
            const dx = p.x - p2.x;
            const dy = p.y - p2.y;
            const distSq = dx * dx + dy * dy;
            if (distSq < LINK_DIST_SQ) {
              const lineOpacity = (1 - Math.sqrt(distSq) / 130) * 0.12;
              ctx.beginPath();
              ctx.moveTo(p.x, p.y);
              ctx.lineTo(p2.x, p2.y);
              ctx.strokeStyle = `hsla(${p.hue}, ${p.saturation}%, ${p.lightness}%, ${lineOpacity})`;
              ctx.lineWidth = 0.6;
              ctx.stroke();
            }
          }
        }
      }

      // 粒子
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        p.pulsePhase += p.pulseSpeed;

        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;

        const pulse = 0.5 + 0.5 * Math.sin(p.pulsePhase);
        const currentOpacity = p.opacity * (0.7 + 0.3 * pulse);

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `hsla(${p.hue}, ${p.saturation}%, ${p.lightness}%, ${currentOpacity})`;
        ctx.fill();
      });

      animationId = requestAnimationFrame(animate);
    };

    animate();

    let resizeTimer;
    const handleResize = () => {
      // 🔥 防抖：避免拖拽窗口时高频重设画布尺寸
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;
      }, 200);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(animationId);
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // 加载小说列表
  const loadNovels = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/novels/user/me', {
        params: { creationType: 'auto' }
      });
      if (response.data && Array.isArray(response.data.data)) {
        setNovels(response.data.data);
      } else if (response.data && Array.isArray(response.data)) {
        setNovels(response.data);
      } else {
        setNovels([]);
      }
    } catch (error) {
      setNovels([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadNovels();
  }, [loadNovels]);

  // 🔥 搜索过滤（标题/简介模糊匹配）
  const filteredNovels = useMemo(() => {
    const kw = searchText.trim().toLowerCase();
    if (!kw) return novels;
    return novels.filter(n =>
      (n.title || '').toLowerCase().includes(kw) ||
      (n.description || '').toLowerCase().includes(kw)
    );
  }, [novels, searchText]);

  // 🔥 搜索变化时重置分批渲染计数
  useEffect(() => {
    setVisibleCount(BATCH_SIZE);
  }, [searchText]);

  // 🔥 IntersectionObserver 无限滚动：哨兵元素进入视口时加载下一批
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVisibleCount(prev => prev + BATCH_SIZE);
        }
      },
      { rootMargin: '400px' } // 提前 400px 预加载，滚动无感
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [filteredNovels.length]);

  // 当前批次应渲染的卡片
  const visibleNovels = useMemo(
    () => filteredNovels.slice(0, visibleCount),
    [filteredNovels, visibleCount]
  );

  // 创建新小说
  const handleCreateNovel = async () => {
    if (!newNovel.title.trim()) return;
    setCreating(true);
    try {
      const response = await apiClient.post('/novels', {
        title: newNovel.title || '未命名小说',
        description: newNovel.description || '',
        creationType: 'auto',
        settings: { genre: '', writingStyle: '', characters: [], worldBuilding: '', plotPoints: [], themes: [] },
        chapters: []
      });

      let createdNovel = response.data.success && response.data.data ? response.data.data : response.data;
      const novelId = createdNovel._id || createdNovel.id;

      if (novelId) {
        setNovels(prev => [{
          id: novelId,
          title: createdNovel.title || newNovel.title,
          description: createdNovel.description || newNovel.description,
          createdAt: createdNovel.createdAt || new Date().toISOString(),
          updatedAt: createdNovel.updatedAt || new Date().toISOString(),
          chapterCount: 0,
          totalWords: 0,
          generationType: 'auto'
        }, ...prev]);

        setOpenDialog(false);
        setNewNovel({ title: '', description: '' });
        navigate(`/novel-editor/${novelId}`);
      }
    } catch (error) {
      // 静默处理（拦截器已统一弹错误提示）
    } finally {
      setCreating(false);
    }
  };

  // 删除小说 - 打开确认弹窗
  const handleDeleteNovel = (e, novel) => {
    e.stopPropagation();
    const novelId = novel._id || novel.id;
    setDeleteDialog({ open: true, novelId, title: novel.title || '未命名项目' });
  };

  const handleConfirmDelete = async () => {
    const id = deleteDialog.novelId;
    try {
      await apiClient.delete(`/novels/${id}`);
      setNovels(prev => prev.filter(n => (n._id || n.id) !== id));
    } catch (err) {
      // 静默处理
    } finally {
      setDeleteDialog({ open: false, novelId: null, title: '' });
    }
  };

  // 编辑标题
  const handleStartEditTitle = (e, novel) => {
    e.stopPropagation();
    const id = novel._id || novel.id;
    setEditingNovelId(id);
    setEditingTitle(novel.title || '');
  };

  const handleSaveTitle = (e) => {
    e.stopPropagation();
    if (!editingNovelId || !editingTitle.trim()) {
      setEditingNovelId(null);
      return;
    }
    const trimmedTitle = editingTitle.trim().slice(0, 30);
    apiClient.put(`/novels/${editingNovelId}`, { title: trimmedTitle }).then(() => {
      setNovels(prev =>
        prev.map(n => ((n._id || n.id) === editingNovelId ? { ...n, title: trimmedTitle } : n))
      );
      setEditingNovelId(null);
    }).catch(err => {
      // 静默处理
    });
  };

  const handleCancelEdit = (e) => {
    e.stopPropagation();
    setEditingNovelId(null);
    setEditingTitle('');
  };

  const handleStartCreating = (e, novelId) => {
    e.stopPropagation();
    navigate(`/novel-editor/${novelId}`);
  };

  // 统计（基于全量数据，不受搜索过滤影响）
  const totalWords = useMemo(
    () => novels.reduce((sum, n) => sum + (n.totalWords || n.wordCount || 0), 0),
    [novels]
  );

  return (
    <Box className="auto-novel-page">
      {/* 粒子背景 */}
      <canvas ref={canvasRef} className="particle-canvas" />

      {/* 顶部导航 */}
      <Fade in={mounted} timeout={600}>
        <Box className="top-nav">
          <Box className="nav-left">
            <Tooltip title="返回首页">
              <IconButton onClick={() => navigate('/dashboard')} className="back-btn">
                <ArrowBackIcon />
              </IconButton>
            </Tooltip>
            <Box className="logo-container">
              <Box className="logo-icon">
                <SmartToyIcon sx={{ fontSize: 24, color: '#fff' }} />
              </Box>
              <Box className="logo-glow" />
            </Box>
            <Box>
              <Typography className="nav-title">全自动小说</Typography>
              <Typography className="nav-subtitle">AI驱动创作引擎</Typography>
            </Box>
          </Box>

          <Box className="nav-right">
            <Button className="create-btn" startIcon={<AddIcon />} onClick={() => setOpenDialog(true)}>
              新建项目
            </Button>
          </Box>
        </Box>
      </Fade>

      {/* 主内容区 */}
      <Box className="main-content">

        {/* Hero区域 */}
        <Box className="hero-section">
          <Box className="hero-decor-line" />
          <Box className="hero-badge">
            <Box className="badge-dot" />
            <Typography className="badge-text">AI驱动的全自动创作引擎</Typography>
            <Box className="badge-dot" />
          </Box>

          <Box className="hero-title-area">
            <Typography className="hero-title-main">
              <Box component="span" className="title-char" style={{ animationDelay: '0s' }}>释</Box>
              <Box component="span" className="title-char" style={{ animationDelay: '0.08s' }}>放</Box>
              <Box component="span" className="title-char" style={{ animationDelay: '0.16s' }}>创</Box>
              <Box component="span" className="title-char" style={{ animationDelay: '0.24s' }}>意</Box>
            </Typography>
            <Box className="hero-title-sub">
              <Box component="span" className="subtitle-line" />
              <Box component="span" className="subtitle-text">让AI为你书写世界</Box>
              <Box component="span" className="subtitle-line" />
            </Box>
          </Box>

          <Box className="hero-description-wrapper">
            <Typography className="hero-description">
              输入小说主题，AI将自动构建
              <Box component="span" className="highlight-phrase">完整故事架构</Box>、
              <Box component="span" className="highlight-phrase">人物设定</Box>与
              <Box component="span" className="highlight-phrase">精彩正文</Box>
            </Typography>
          </Box>

          <Box className="feature-tags">
            <Box className="feature-tag tag-cyan">
              <AutoStoriesIcon className="feature-icon" />
              <Typography className="tag-text">智能大纲生成</Typography>
            </Box>
            <Box className="feature-tag tag-purple">
              <AutoStoriesIcon className="feature-icon" />
              <Typography className="tag-text">批量章节创作</Typography>
            </Box>
            <Box className="feature-tag tag-blue">
              <AutoAwesomeIcon className="feature-icon" />
              <Typography className="tag-text">多风格适配</Typography>
            </Box>
          </Box>

          <Box className="hero-bottom-decor">
            <Box className="decor-orb orb-1" />
            <Box className="decor-orb orb-2" />
            <Box className="decor-orb orb-3" />
          </Box>
        </Box>

        {/* 项目列表 */}
        <Box className="projects-section">
          <Box className="section-header">
            <Box className="section-title-group">
              <Typography className="section-title">我的创作项目</Typography>
            </Box>
            <Box className="section-meta">
              {novels.length > 6 && (
                <TextField
                  className="project-search"
                  size="small"
                  placeholder="搜索项目..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 17, color: 'rgba(255,255,255,0.35)' }} />
                      </InputAdornment>
                    ),
                    endAdornment: searchText ? (
                      <InputAdornment position="end" onClick={() => setSearchText('')} sx={{ cursor: 'pointer' }}>
                        <CloseIcon sx={{ fontSize: 15, color: 'rgba(255,255,255,0.35)', '&:hover': { color: 'rgba(255,255,255,0.7)' } }} />
                      </InputAdornment>
                    ) : null
                  }}
                />
              )}
              <Typography className="section-count">
                {searchText ? `${filteredNovels.length} / ${novels.length} 个项目` : `${novels.length} 个项目`}
              </Typography>
              {totalWords > 0 && (
                <Typography className="section-words">
                  共计 {fmtWords(totalWords)} 字
                </Typography>
              )}
            </Box>
          </Box>

          {loading ? (
            <Box className="loading-container">
              <Box className="loading-orb">
                <CircularProgress className="loading-spinner" size={48} />
              </Box>
              <Typography className="loading-text">正在加载您的创作...</Typography>
            </Box>
          ) : novels.length === 0 ? (
            <Box className="empty-state">
              <Box className="empty-bg-orb" />
              <Box className="empty-icon-ring">
                <Box className="empty-icon-inner">
                  <SmartToyIcon sx={{ fontSize: 48 }} />
                </Box>
                <Box className="empty-icon-ring-outer" />
              </Box>
              <Typography className="empty-title">开启您的创作之旅</Typography>
              <Typography className="empty-desc">
                点击下方按钮，让AI协助您创作第一部小说
              </Typography>
              <Button className="create-first-btn" onClick={() => setOpenDialog(true)}>
                <AddIcon sx={{ fontSize: 18, mr: 0.5 }} />
                创建第一个项目
              </Button>
            </Box>
          ) : filteredNovels.length === 0 ? (
            <Box className="empty-state search-empty">
              <SearchOffIcon sx={{ fontSize: 42, color: 'rgba(255,255,255,0.2)' }} />
              <Typography className="empty-title">未找到匹配的项目</Typography>
              <Typography className="empty-desc">
                试试其他关键词，或清空搜索查看全部项目
              </Typography>
            </Box>
          ) : (
            <>
              <Box className="projects-grid">
                {visibleNovels.map((novel, index) => (
                  <ProjectCard
                    key={novel._id || novel.id}
                    novel={novel}
                    index={index}
                    isEditing={editingNovelId === (novel._id || novel.id)}
                    editingTitle={editingTitle}
                    onEditingTitleChange={setEditingTitle}
                    onStartEdit={handleStartEditTitle}
                    onSaveTitle={handleSaveTitle}
                    onCancelEdit={handleCancelEdit}
                    onDelete={handleDeleteNovel}
                    onStart={handleStartCreating}
                  />
                ))}

                <Box className="add-card" onClick={() => setOpenDialog(true)}>
                  <Box className="add-bg-ring" />
                  <Box className="add-icon-wrap">
                    <AddIcon sx={{ fontSize: 32 }} />
                  </Box>
                  <Typography className="add-text">创建新项目</Typography>
                  <Typography className="add-subtext">开启AI创作之旅</Typography>
                </Box>
              </Box>

              {/* 🔥 无限滚动哨兵：滚动到底部自动加载下一批（配加载指示器） */}
              {visibleCount < filteredNovels.length && (
                <Box ref={sentinelRef} className="load-more-sentinel">
                  <CircularProgress size={22} thickness={4} sx={{ color: 'rgba(99,165,250,0.5)' }} />
                  <Typography className="load-more-text">
                    加载更多（{visibleCount}/{filteredNovels.length}）
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>

      {/* 新建项目对话框 */}
      <Dialog
        open={openDialog}
        onClose={() => !creating && setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ className: 'NexusDialog-paper' }}
      >
        <DialogTitle className="NexusDialog-title">
          <Box className="NexusDialog-titleIcon">
            <SmartToyIcon sx={{ fontSize: 18 }} />
          </Box>
          创建新项目
        </DialogTitle>
        <DialogContent className="NexusDialog-content">
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              label="项目标题"
              placeholder="例如：穿越小说"
              value={newNovel.title}
              onChange={(e) => setNewNovel(prev => ({ ...prev, title: e.target.value }))}
              className="cyber-input"
              autoFocus
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="简短描述（可选）"
              placeholder="描述该项目的信息..."
              value={newNovel.description}
              onChange={(e) => setNewNovel(prev => ({ ...prev, description: e.target.value }))}
              className="cyber-input"
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions className="NexusDialog-actions">
          <Button
            className="NexusDialog-cancelBtn"
            onClick={() => setOpenDialog(false)}
            disabled={creating}
          >
            取消
          </Button>
          <Button
            className="NexusDialog-confirmBtn"
            onClick={handleCreateNovel}
            disabled={!newNovel.title.trim() || creating}
          >
            {creating ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : '开始创作'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 删除确认对话框 */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, novelId: null, title: '' })}
        PaperProps={{
          sx: {
            backgroundColor: 'var(--nexus-bg-card)',
            backdropFilter: 'blur(24px)',
            border: '1px solid var(--nexus-border)',
            borderRadius: '16px',
            boxShadow: '0 0 60px rgba(0,0,0,0.6)',
            minWidth: { xs: 280, sm: 340 }
          }
        }}
      >
        <DialogTitle sx={{ color: 'var(--nexus-text-primary)', fontWeight: 700, fontSize: '1rem', pb: 1 }}>
          确认删除项目
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          <Typography sx={{ color: 'var(--nexus-text-secondary)', fontSize: '0.9rem', lineHeight: 1.6 }}>
            确定删除「<Box component="span" sx={{ color: 'var(--nexus-text-primary)', fontWeight: 600 }}>{deleteDialog.title}</Box>」吗？此操作无法撤销。
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setDeleteDialog({ open: false, novelId: null, title: '' })}
            sx={{
              textTransform: 'none',
              color: 'var(--nexus-text-muted)',
              border: '1px solid var(--nexus-border)',
              borderRadius: '8px',
              '&:hover': { borderColor: 'var(--nexus-border-hover)' }
            }}
          >
            取消
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            sx={{
              textTransform: 'none',
              backgroundColor: 'var(--nexus-error)',
              borderRadius: '8px',
              fontWeight: 600,
              '&:hover': { backgroundColor: '#dc2626' }
            }}
          >
            确认删除
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AutoNovelPage;
